import { type ReadinessResponse, readinessResponseSchema } from '@goodway/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestJson } from './client';
import type { RoleGrant } from './session';

export interface RoleRow {
  id: string;
  role: RoleGrant['role'];
  departmentId: string | null;
  departmentName: string | null;
  reason: string;
  grantedAt: string;
  userId: string;
  displayName: string;
  employeeId: string | null;
  position: string | null;
}

export interface CoHeadRow {
  id: string;
  departmentId: string;
  departmentName: string;
  employeeId: string;
  fullName: string;
  position: string | null;
  reason: string;
  createdAt: string;
}

export interface SecurityEventRow {
  id: number;
  occurredAt: string;
  eventType: 'login' | 'logout' | 'access_denied' | 'session_rejected';
  action: string | null;
  resourceType: string | null;
  ipAddress: string | null;
  details: Record<string, unknown>;
  displayName: string | null;
}

export const useRoles = () =>
  useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => requestJson<{ items: RoleRow[] }>('/api/v1/admin/roles'),
  });

export const useCoHeads = () =>
  useQuery({
    queryKey: ['admin', 'coHeads'],
    queryFn: () => requestJson<{ items: CoHeadRow[] }>('/api/v1/admin/department-heads'),
  });

export const useSecurityEvents = () =>
  useQuery({
    queryKey: ['admin', 'securityEvents'],
    queryFn: () => requestJson<{ items: SecurityEventRow[] }>('/api/v1/admin/security-events'),
    refetchInterval: 30_000,
  });

export const useSystemStatus = () =>
  useQuery({
    queryKey: ['admin', 'system'],
    queryFn: async (): Promise<ReadinessResponse> =>
      readinessResponseSchema.parse(await requestJson<unknown>('/api/v1/admin/system')),
    refetchInterval: 15_000,
  });

function useAdminMutation<T>(path: (input: T) => string, method: 'POST' | 'DELETE', invalidate: string[][]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: T) =>
      requestJson<unknown>(path(input), {
        method,
        body: method === 'POST' ? JSON.stringify(input) : undefined,
      }),
    onSuccess: async () => {
      for (const key of invalidate) await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export const useGrantRole = () =>
  useAdminMutation<{ employeeId: string; role: RoleGrant['role']; departmentId?: string; reason: string }>(
    () => '/api/v1/admin/roles',
    'POST',
    [['admin', 'roles'], ['me']],
  );

export const useRevokeRole = () =>
  useAdminMutation<{ id: string }>((input) => `/api/v1/admin/roles/${input.id}`, 'DELETE', [
    ['admin', 'roles'],
    ['me'],
  ]);

export const useAddCoHead = () =>
  useAdminMutation<{ departmentId: string; employeeId: string; reason: string }>(
    () => '/api/v1/admin/department-heads',
    'POST',
    [['admin', 'coHeads'], ['org']],
  );

export const useRemoveCoHead = () =>
  useAdminMutation<{ id: string }>((input) => `/api/v1/admin/department-heads/${input.id}`, 'DELETE', [
    ['admin', 'coHeads'],
    ['org'],
  ]);

export interface AttendanceStatus {
  sources: { id: string; name: string; lastUsedAt: string | null }[];
  reports: {
    sourceId: string;
    receivedAt: string;
    rows: number;
    inserted: number;
    updated: number;
    unchanged: number;
    errors: number;
    /** Загрузил вручную; null — прислал коннектор. */
    uploadedBy: string | null;
  }[];
  unmatched: { sourceId: string; deviceId: string; deviceName: string; lastSeen: string | null }[];
}

export function useAttendanceStatus() {
  return useQuery({
    queryKey: ['admin', 'attendance'],
    queryFn: () => requestJson<AttendanceStatus>('/api/v1/admin/attendance'),
    refetchInterval: 60_000,
  });
}
